import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'primereact/toast';
import { useRef } from 'react';
import { 
    fetchJenkinsPlans, 
    deleteJenkinsPlan, 
    updateJenkinsPlan, 
    saveJenkinsPlan,
    handleUnauthorized 
} from './DataFlow';
import ParameterDefinitionEditor from '../templating/ParameterDefinitionEditor';
import { confirmDialog } from 'primereact/confirmdialog';
import { ConfirmDialog } from 'primereact/confirmdialog';

const JenkinsPlans = () => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [displayDialog, setDisplayDialog] = useState(false);
    const [newPlan, setNewPlan] = useState({
        name: '',
        apiUrl: '',
        webhookUrl: '',
        rawJson: '',
        parameters: []
    });
    const toast = useRef(null);
    const baseURL = process.env.REACT_APP_SSP_BACKEND_URL;
    const [editingPlan, setEditingPlan] = useState(null);
    const [editDialogVisible, setEditDialogVisible] = useState(false);
    const isPowerUser = localStorage.getItem('isAppPowerUser') === 'true';

    useEffect(() => {
        loadPlans();
    }, []);

    const loadPlans = async () => {
        setLoading(true);
        await fetchJenkinsPlans(setPlans, handleUnauthorized);
        setLoading(false);
    };

    const parseJsonForParameters = (jsonString) => {
        try {
            const parsed = JSON.parse(jsonString);
            const propertyArray = parsed.property || [];
            const paramProperty = propertyArray.find((prop) => prop.parameterDefinitions);
            
            if (paramProperty && paramProperty.parameterDefinitions) {
                return paramProperty.parameterDefinitions.map(param => ({
                    name: param.name,
                    type: param.type,
                    defaultParameterValue: {
                        value: param.defaultParameterValue?.value || ""
                    },
                    choices: param.choices || []
                }));
            }
            return [];
        } catch (error) {
            console.error('Error parsing JSON:', error);
            return [];
        }
    };

    const handleEdit = (plan) => {
        let parameters = plan.parameters;
        if ((!parameters || parameters.length === 0) && plan.rawJson) {
            parameters = parseJsonForParameters(plan.rawJson);
        }
        
        setEditingPlan({
            ...plan,
            parameters: parameters
        });
        setEditDialogVisible(true);
    };

    const handleDelete = (rowData) => {
        confirmDialog({
            message: (
                <div>
                    <p>Are you sure you want to delete this Jenkins Plan?</p>
                    <p style={{ color: 'var(--red-500)', fontWeight: 'bold' }}>
                        Warning: This will also delete all related configurations:
                    </p>
                    <ul style={{ marginLeft: '20px' }}>
                        <li>Plan Instances</li>
                        <li>Scheduler Configurations</li>
                        <li>Trigger Configurations</li>
                    </ul>
                    <p style={{ marginTop: '10px' }}>This action cannot be undone.</p>
                </div>
            ),
            header: 'Delete Confirmation',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    const success = await deleteJenkinsPlan(rowData._id, handleUnauthorized);
                    if (success) {
                        toast.current.show({
                            severity: 'success',
                            summary: 'Success',
                            detail: 'Plan and related configurations deleted successfully'
                        });
                        loadPlans();
                    } else {
                        toast.current.show({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to delete plan'
                        });
                    }
                } catch (error) {
                    console.error('Error deleting plan:', error);
                    toast.current.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to delete plan'
                    });
                }
            },
            reject: () => {} // Do nothing when rejected
        });
    };

    const handleUpdate = async (plan) => {
        try {
            const success = await updateJenkinsPlan(plan._id, handleUnauthorized);
            if (success) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Plan updated successfully'
                });
                loadPlans();
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to update plan'
                });
            }
        } catch (error) {
            console.error('Error updating plan:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to update plan'
            });
        }
    };

    const handleParametersUpdate = async (updatedParameters) => {
        try {
            const updatedPlan = {
                ...editingPlan,
                parameters: updatedParameters
            };

            const result = await saveJenkinsPlan(updatedPlan, handleUnauthorized);
            
            if (result.success) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Parameters updated successfully'
                });
                setEditDialogVisible(false);
                loadPlans();
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to update parameters'
                });
            }
        } catch (error) {
            console.error('Error updating parameters:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to update parameters'
            });
        }
    };

    const validateJson = (jsonString) => {
        try {
            JSON.parse(jsonString);
            return true;
        } catch (e) {
            return false;
        }
    };

    const savePlan = async () => {
        try {
            // Validate JSON before saving
            if (newPlan.rawJson && !validateJson(newPlan.rawJson)) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Invalid JSON format'
                });
                return;
            }

            const result = await saveJenkinsPlan(newPlan, handleUnauthorized);
            
            if (result.success) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: `Plan ${newPlan._id ? 'updated' : 'created'} successfully`
                });
                setDisplayDialog(false);
                loadPlans();
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: `Failed to ${newPlan._id ? 'update' : 'create'} plan`
                });
            }
        } catch (error) {
            console.error('Error saving plan:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: `Failed to ${newPlan._id ? 'update' : 'create'} plan`
            });
        }
    };

    const jsonBodyTemplate = (rowData) => {
        const jsonLength = rowData.rawJson ? rowData.rawJson.length : 0;
        
        return (
            <div style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {jsonLength > 0 ? (
                    <span>{jsonLength} characters</span>
                ) : (
                    <span className="text-gray-500 italic">No JSON data</span>
                )}
            </div>
        );
    };

    const urlBodyTemplate = (rowData) => {
        const renderUrl = (url, label) => {
            if (!url) {
                return (
                    <div style={{ 
                        maxWidth: '300px',
                        lineHeight: '1.2',
                        marginBottom: '8px'
                    }}>
                        <div style={{ fontSize: '0.8em', color: 'var(--text-color-secondary)' }}>
                            {label}:
                        </div>
                        <div style={{ color: 'var(--text-color-secondary)', fontStyle: 'italic' }}>
                            No URL provided
                        </div>
                    </div>
                );
            }

            let domain = '';
            let path = '';
            
            try {
                const urlObj = new URL(url);
                domain = urlObj.origin;
                path = urlObj.pathname + urlObj.search;
            } catch (error) {
                console.warn(`Invalid URL: ${url}`);
                return (
                    <div style={{ 
                        maxWidth: '300px',
                        lineHeight: '1.2',
                        marginBottom: '8px'
                    }}>
                        <div style={{ fontSize: '0.8em', color: 'var(--text-color-secondary)' }}>
                            {label}:
                        </div>
                        <div style={{ 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: 'var(--red-500)'
                        }}>
                            {url} (Invalid URL)
                        </div>
                    </div>
                );
            }
            
            return (
                <div style={{ 
                    maxWidth: '300px',
                    lineHeight: '1.2',
                    marginBottom: '8px'
                }}>
                    <div style={{ fontSize: '0.8em', color: 'var(--text-color-secondary)' }}>
                        {label}:
                    </div>
                    <a 
                        href={url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        title={url}
                        style={{ 
                            textDecoration: 'none',
                            color: 'var(--primary-color)'
                        }}
                    >
                        <div style={{ 
                            fontWeight: 'bold',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            {domain}
                        </div>
                        <div style={{ 
                            fontSize: '0.9em',
                            color: 'var(--text-color-secondary)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            {path}
                        </div>
                    </a>
                </div>
            );
        };

        return (
            <div>
                {renderUrl(rowData.apiUrl, 'API URL')}
                {renderUrl(rowData.webhookUrl, 'Webhook URL')}
            </div>
        );
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                <Button 
                    icon="pi pi-pencil" 
                    className="p-button-rounded p-button-text"
                    onClick={() => handleEdit(rowData)} 
                    tooltip="Edit"
                    tooltipOptions={{ position: 'top' }}
                    style={{ padding: '0.5rem', fontSize: '1rem' }}
                />
                <Button 
                    icon="pi pi-trash" 
                    className="p-button-rounded p-button-danger p-button-text"
                    onClick={() => handleDelete(rowData)}
                    tooltip="Delete"
                    tooltipOptions={{ position: 'top' }}
                    style={{ padding: '0.5rem', fontSize: '1rem' }}
                />
                <Button 
                    icon="pi pi-refresh" 
                    className="p-button-rounded p-button-text"
                    onClick={() => handleUpdate(rowData)}
                    tooltip="Update"
                    tooltipOptions={{ position: 'top' }}
                    style={{ padding: '0.5rem', fontSize: '1rem' }}
                />
            </div>
        );
    };

    const renderDialogFooter = () => {
        return (
            <div>
                <Button 
                    label="Cancel" 
                    icon="pi pi-times" 
                    className="p-button-text" 
                    onClick={() => setDisplayDialog(false)} 
                />
                <Button 
                    label="Save" 
                    icon="pi pi-check" 
                    className="p-button-text" 
                    onClick={savePlan} 
                />
            </div>
        );
    };

    // If not a power user, show message
    if (!isPowerUser) {
        return (
            <div className="card">
                <div className="flex align-items-center justify-content-center" style={{ minHeight: '300px' }}>
                    <div className="text-center">
                        <i className="pi pi-lock text-5xl mb-3" style={{ color: '#FFA726' }}></i>
                        <div className="text-2xl font-bold mb-2">Power User Access Required</div>
                        <div className="text-gray-600">You need Power User permissions to access Jenkins Plans.</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <style>
                {`
                    .p-button.p-button-text:not(.p-button-danger) {
                        color: var(--primary-color);
                    }
                    .p-button.p-button-text:not(.p-button-danger):hover {
                        background: rgba(var(--primary-color-rgb), 0.04);
                    }
                    .p-button.p-button-text.p-button-danger {
                        color: var(--red-500);
                    }
                    .p-button.p-button-text.p-button-danger:hover {
                        background: rgba(var(--red-500-rgb), 0.04);
                    }
                    .p-button.p-button-rounded {
                        width: 2rem;
                        height: 2rem;
                    }
                    .p-button .p-button-icon {
                        font-size: 1rem;
                    }
                `}
            </style>
            <Toast ref={toast} />
            <ConfirmDialog />
            
            <div className="grid">
                <div className="col-12">
                    <div className="card">
                        <DataTable 
                            value={plans} 
                            loading={loading}
                            paginator 
                            rows={10} 
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            className="p-datatable-striped"
                            scrollable 
                            scrollHeight="400px"
                            header={
                                <div className="flex justify-content-between align-items-center">
                                    <h5 className="m-0">Jenkins Plans</h5>
                                    <Button 
                                        icon="pi pi-plus"
                                        className="p-button-success" 
                                         style={{ width: '100px' }}
                                        onClick={() => {
                                            setNewPlan({ name: '', apiUrl: '', webhookUrl: '', rawJson: '', parameters: [] });
                                            setDisplayDialog(true);
                                        }} 
                                    />
                                </div>
                            }
                        >
                            <Column field="name" header="Name" sortable />
                            <Column field="urls" header="URLs" body={urlBodyTemplate} style={{ width: '350px' }} />
                            <Column 
                                field="parameters" 
                                header="Parameters Count" 
                                sortable
                                body={(rowData) => rowData.parameters?.length || 0} 
                            />
                            <Column 
                                field="rawJson" 
                                header="Raw JSON" 
                                body={jsonBodyTemplate}
                                style={{ width: '300px' }}
                            />
                            <Column body={actionBodyTemplate} header="Actions" style={{ width: '200px' }} />
                        </DataTable>
                    </div>
                </div>
            </div>

            <Dialog 
                visible={displayDialog} 
                style={{ width: '600px' }} 
                header={newPlan._id ? "Edit Jenkins Plan" : "Add New Jenkins Plan"} 
                modal 
                className="p-fluid" 
                footer={renderDialogFooter()} 
                onHide={() => setDisplayDialog(false)}
            >
                <div className="field">
                    <label htmlFor="name">Name</label>
                    <InputText 
                        id="name" 
                        value={newPlan.name} 
                        onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })} 
                        required 
                        autoFocus 
                    />
                </div>
                <div className="field">
                    <label htmlFor="apiUrl">API URL</label>
                    <InputText 
                        id="apiUrl" 
                        value={newPlan.apiUrl} 
                        onChange={(e) => setNewPlan({ ...newPlan, apiUrl: e.target.value })} 
                        required 
                    />
                </div>
                <div className="field">
                    <label htmlFor="webhookUrl">Webhook URL</label>
                    <InputText 
                        id="webhookUrl" 
                        value={newPlan.webhookUrl} 
                        onChange={(e) => setNewPlan({ ...newPlan, webhookUrl: e.target.value })} 
                        required 
                    />
                </div>
                <div className="field">
                    <label htmlFor="rawJson">Raw JSON</label>
                    <InputTextarea 
                        id="rawJson" 
                        value={newPlan.rawJson} 
                        onChange={(e) => setNewPlan({ ...newPlan, rawJson: e.target.value })} 
                        rows={10} 
                        className="font-mono"
                        placeholder="Enter JSON data here..."
                    />
                </div>
            </Dialog>

            {/* Parameters Editor Dialog */}
            <Dialog
                visible={editDialogVisible}
                style={{ width: '80vw' }}
                header="Edit Jenkins Plan Parameters"
                modal
                onHide={() => setEditDialogVisible(false)}
            >
                {editingPlan && (
                    <div>
                        <div className="mb-4">
                            <h3>{editingPlan.name}</h3>
                            <p><strong>API URL:</strong> {editingPlan.apiUrl}</p>
                            <p><strong>Webhook URL:</strong> {editingPlan.webhookUrl}</p>
                        </div>
                        
                        <ParameterDefinitionEditor
                            paramDefinitions={editingPlan.parameters}
                            onUpdate={handleParametersUpdate}
                        />
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default JenkinsPlans;
